import React, { useState, useEffect } from "react";
import moment from "moment";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { contactsService } from "../../../Services/contactsService";
import APIRequest from "../../../Services/commonAPIConsumer";
import TrackSortingPopup from "./Modals/TrackSortingPopup";
import DLReaderStatsModal from "./DLReaderStatsModal";
import LoadingSpinner from "../../Controls/Spinner/LoadingSpinner";

export const buildDLListFromRecipients = (recipientsList = [], contactGroups = []) => {
  if (!Array.isArray(recipientsList) || recipientsList.length === 0) return [];

  const isDL = (r) => {
    const val = r?.isDLGroup ?? r?.IsDLGroup;
    return val === true;
  };

  const getDLReference = (r) => {
    if (!r) return "";
    return (r.dlEmail || r.DLEmail || "").trim().toLowerCase();
  };

  const isDLRecipient = (r) => isDL(r) && (!getDLReference(r) || getDLReference(r) === (r.email || r.Email || "").trim().toLowerCase());

  const dlRecipients = recipientsList.filter(isDLRecipient);
  if (dlRecipients.length === 0) return [];

  return dlRecipients.map((dlRec, idx) => {
    const dlEmail = (dlRec.email || "").trim();
    const dlEmailLower = dlEmail.toLowerCase();

    const matchedGroup = (contactGroups || []).find((g) => {
      const alias = (g?.aliasEmail || g?.AliasEmail || "").trim().toLowerCase();
      return alias === dlEmailLower;
    });

    const groupName =
      matchedGroup?.name ||
      matchedGroup?.Name ||
      (dlRec.firstName ? `${dlRec.firstName || ""} ${dlRec.lastName || ""}`.trim() : dlEmail.split("@")[0] || `Distribution List ${idx + 1}`);

    const allowedDomains =
      matchedGroup?.domains ||
      matchedGroup?.Domains ||
      dlRec.allowedDomains ||
      dlRec.AllowedDomains ||
      [];

    const memberRecipients = recipientsList.filter((r) => {
      const email = (r.email || r.Email || "").trim().toLowerCase();
      if (!email || email === dlEmailLower) return false;
      const hasCode = Boolean((r?.code || "").toString().trim());
      if (!hasCode) return false;
      if (!isDL(r) && !getDLReference(r)) return false;
      const ref = getDLReference(r);
      if (ref) return ref === dlEmailLower;

      if (matchedGroup && isDL(r)) {
        const members = matchedGroup.members || matchedGroup.Members || matchedGroup.memberContactIds || matchedGroup.contacts || matchedGroup.Contacts || [];
        if (members.length > 0) {
          const isMember = members.some((m) => {
            const mAddr = (typeof m === "string" ? m : (m?.email || m?.Email || m?.address || m?.Address || "")).trim().toLowerCase();
            return mAddr === email;
          });
          if (isMember) return true;
        }
      }

      return isDL(r) && dlRecipients.length === 1;
    });

    const groupMembers = memberRecipients.map((m, mIdx) => {
      const mEmail = m.email || m.Email || "";
      const readCount = Number(m.readCount !== undefined ? m.readCount : (m.ReadCount !== undefined ? m.ReadCount : 0));
      const isEnabled = m.enabled !== undefined ? m.enabled : (m.Enabled !== undefined ? m.Enabled : true);
      const statusStr = m.status || m.Status || (isEnabled ? "Active" : "Banned");
      const isBanned = statusStr === "Banned" || isEnabled === false;
      const rawVote = m.vote !== undefined ? m.vote : (m.Vote !== undefined ? m.Vote : null);
      let voteVal = null;
      if (rawVote === 1 || rawVote === "1" || rawVote === "Like") voteVal = 1;
      else if (rawVote === 0 || rawVote === "0" || rawVote === "Dislike") voteVal = 0;

      return {
        id: m.id || m._id || mEmail || `member-${idx}-${mIdx}`,
        email: mEmail,
        code: m.code || "",
        firstName: m.firstName || m.FirstName || "",
        lastName: m.lastName || m.LastName || "",
        mobileCountryCode: m.mobileCountryCode || m.MobileCountryCode || "",
        mobileNumber: m.mobileNumber || "",
        openedTime: m.openedTime || "",
        readCount: readCount,
        totalTime: m.totalTime || m.TotalTime || "00554:00:00",
        vote: voteVal,
        enabled: !isBanned,
        status: isBanned ? "Banned" : "Active",
        banned: isBanned,
        daysFirstAuthrized: m.daysFirstAuthrized !== undefined ? m.daysFirstAuthrized : (m.DaysFirstAuthrized !== undefined ? m.DaysFirstAuthrized : 0),
        daysSinceFirstView: m.daysSinceFirstView !== undefined ? m.daysSinceFirstView : (m.DaysSinceFirstView !== undefined ? m.DaysSinceFirstView : 0),
        daysSinceLastView: m.daysSinceLastView !== undefined ? m.daysSinceLastView : (m.DaysSinceLastView !== undefined ? m.DaysSinceLastView : 0),
        printCount: m.printCount !== undefined ? m.printCount : (m.PrintCount !== undefined ? m.PrintCount : 0),
        eDisclosureAgreed: m.eDisclosureAgreed !== undefined ? m.eDisclosureAgreed : null,
        eDisclosureTime: m.eDisclosureTime || null,
        reference: m.reference || m.Reference || "",
        authType: m.authType || m.AuthType || "",
        maxPrintsPerReader: m.maxPrintsPerReader || m.MaxPrintsPerReader || ""
      };
    });

    return {
      id: dlRec.id || dlRec._id || dlEmail || `dl-${idx}`,
      email: dlEmail,
      name: groupName,
      isGroupAlias: true,
      isDistributionList: true,
      IsDLGroup: true,
      allowedDomains: Array.isArray(allowedDomains) ? allowedDomains : [],
      groupMembers: groupMembers
    };
  });
};

export default function DistributionListsSection({
  documentId,
  documentName = "",
  isRPDDL = true,
  initialDLList,
  recipients,
  onDLListChange,
  selectedLanguage,
  timeZoneAbbrevation,
  timeZoneRef,
  applyTimeZoneToDisplay,
  dateFormatValueUpper = "DD/MM/YYYY",
  onOpenReaderStats,
  searchQuery = "",
  canEdit = true,
  handlerUserActiveInactive,
  handleRefreshTrackInterActivity,
  setOpen,
  contactGroups = [],
  onMemberSortChange
}) {
  const [contactGroupsList, setContactGroupsList] = useState(contactGroups || []);

  useEffect(() => {
    if (contactGroups && contactGroups.length > 0) {
      setContactGroupsList(contactGroups);
    }
  }, [contactGroups]);

  const [isDLLoading, setIsDLLoading] = useState(false);

  const [dlList, setDlList] = useState(() => {
    if (recipients && recipients.length > 0) {
      const built = buildDLListFromRecipients(recipients, contactGroups || []);
      if (built.length > 0) return built;
    }
    if (initialDLList && initialDLList.length > 0) return initialDLList;
    return [];
  });

  const getDLAllowedDomains = (dl) => {
    const dlEmail = (dl?.email || dl?.Email || "").trim().toLowerCase();
    if (dlEmail && contactGroupsList && contactGroupsList.length > 0) {
      const matchedGroup = contactGroupsList.find((g) => {
        const alias = (g?.aliasEmail || g?.AliasEmail || "").trim().toLowerCase();
        return alias === dlEmail;
      });
      if (matchedGroup) {
        const doms = matchedGroup.domains || matchedGroup.Domains;
        if (Array.isArray(doms) && doms.length > 0) {
          return doms;
        }
      }
    }
    if (dl?.allowedDomains && Array.isArray(dl.allowedDomains) && dl.allowedDomains.length > 0) {
      return dl.allowedDomains;
    }
    if (dl?.domains && Array.isArray(dl.domains) && dl.domains.length > 0) {
      return dl.domains;
    }
    if (dl?.Domains && Array.isArray(dl.Domains) && dl.Domains.length > 0) {
      return dl.Domains;
    }
    return [];
  };

  const getDLDisplayName = (dl) => {
    const rawEmail = (dl?.email || dl?.Email || dl?.aliasEmail || dl?.AliasEmail || "").trim();
    if (!rawEmail) return "";
    if (rawEmail.includes("<") && rawEmail.includes(">")) {
      return rawEmail;
    }
    const dlEmailLower = rawEmail.toLowerCase();

    let groupName = "";
    if (contactGroupsList && contactGroupsList.length > 0) {
      const matchedGroup = contactGroupsList.find((g) => {
        const alias = (g?.aliasEmail || g?.AliasEmail || "").trim().toLowerCase();
        return alias === dlEmailLower;
      });
      if (matchedGroup && (matchedGroup.name || matchedGroup.Name)) {
        groupName = (matchedGroup.name || matchedGroup.Name).trim();
      }
    }

    if (!groupName) {
      const candidateName = (dl?.name || dl?.Name || "").trim();
      if (
        candidateName &&
        candidateName.toLowerCase() !== dlEmailLower &&
        !candidateName.toLowerCase().startsWith("distribution list")
      ) {
        groupName = candidateName;
      } else if (dl?.firstName || dl?.FirstName) {
        const fn = (dl.firstName || dl.FirstName || "").trim();
        const ln = (dl.lastName || dl.LastName || "").trim();
        const combined = `${fn} ${ln}`.trim();
        if (
          combined &&
          combined.toLowerCase() !== dlEmailLower &&
          !combined.toLowerCase().startsWith("distribution list")
        ) {
          groupName = combined;
        }
      }
    }

    if (groupName) {
      return `${groupName} <${rawEmail}>`;
    }
    return rawEmail;
  };

  const [expandedDL, setExpandedDL] = useState({});
  const [expandedMembers, setExpandedMembers] = useState({});
  const [checkedMembers, setCheckedMembers] = useState({});
  const [selectedMemberForStats, setSelectedMemberForStats] = useState(null);

  // Member Search and Sort state per DL
  const [memberSearch, setMemberSearch] = useState({});
  const [memberSort, setMemberSort] = useState({});
  const getDefaultDLSortOptions = () => [
    {
      label: `${selectedLanguage?.LstLanguageCodes?.UI_FirstRead}${timeZoneAbbrevation?.current ? ` (${timeZoneAbbrevation.current})` : " (IST)"
        }`,
      value: "first_read",
      order: "desc",
      active: "true"
    },
    {
      label: selectedLanguage?.LstLanguageCodes?.UI_TotalMinutes,
      value: "total_time",
      order: "asc",
      active: "false"
    },
    {
      label: selectedLanguage?.LstLanguageCodes?.UI_Reads,
      value: "reads",
      order: "asc",
      active: "false"
    },
    {
      label: selectedLanguage?.LstLanguageCodes?.UI_DaysSinceFirstAuthorized,
      value: "days_since_firstAuth",
      order: "asc",
      active: "false"
    },
    {
      label: selectedLanguage?.LstLanguageCodes?.UI_DaysSinceFirstSActivity,
      value: "days_since_first",
      order: "asc",
      active: "false"
    },
    {
      label: selectedLanguage?.LstLanguageCodes?.UI_DaysSinceLastActivity,
      value: "days_since_last",
      order: "asc",
      active: "false"
    }
  ];

  const [dlSortingPopUp, setDlSortingPopUp] = useState(false);
  const [sortingDLIndex, setSortingDLIndex] = useState(null);
  const [dlSortOptions, setDlSortOptions] = useState(getDefaultDLSortOptions);

  const handleOpenDLSortPopup = (dlIdx) => {
    setSortingDLIndex(dlIdx);
    const current = memberSort[dlIdx] || { value: "first_read", order: "desc" };
    const baseOptions = getDefaultDLSortOptions();
    setDlSortOptions(
      baseOptions.map((opt) =>
        opt.value === current.value
          ? { ...opt, order: current.order, active: "true" }
          : { ...opt, order: "asc", active: "false" }
      )
    );
    setDlSortingPopUp(true);
  };

  const toggleDLSortOrder = (event, value) => {
    if (
      event.target.id === "chkNopleakers" ||
      event.target.id === "sortOptionsDivLabel" ||
      event.target.id === "sortOptionsDiv"
    ) {
      setDlSortOptions((prevOptions) =>
        prevOptions.map((option) =>
          option.value === value
            ? { ...option, active: "true" }
            : { ...option, order: "asc", active: "false" }
        )
      );
    } else {
      setDlSortOptions((prevOptions) =>
        prevOptions.map((option) =>
          option.value === value
            ? { ...option, order: option.order === "asc" ? "desc" : "asc", active: "true" }
            : { ...option, order: "asc", active: "false" }
        )
      );
    }
  };

  const handleCancelDLSorting = () => {
    setDlSortingPopUp(false);
    setSortingDLIndex(null);
  };

  const handleOkDLSorting = () => {
    if (sortingDLIndex !== null && sortingDLIndex !== undefined) {
      const activeOption = dlSortOptions.find((opt) => opt.active === "true");
      if (activeOption) {
        const newSort = {
          value: activeOption.value,
          order: activeOption.order,
          label: activeOption.label
        };
        setMemberSort((prev) => ({ ...prev, [sortingDLIndex]: newSort }));
        const dl = dlList[sortingDLIndex];
        const dlEmail = (dl?.Address || dl?.email || dl?.Email || '').trim().toLowerCase();
        if (typeof onMemberSortChange === 'function') {
          onMemberSortChange(documentId, sortingDLIndex, newSort, dlEmail);
        }
      }
    }
    setDlSortingPopUp(false);
    setSortingDLIndex(null);
  };

  useEffect(() => {
    if (recipients && recipients.length > 0) {
      const built = buildDLListFromRecipients(recipients, contactGroupsList);
      if (built.length > 0) {
        setDlList(built);
        return;
      }
    }
    if (initialDLList && initialDLList.length > 0) {
      setDlList(initialDLList);
    } else {
      setDlList([]);
    }
  }, [recipients, initialDLList, contactGroupsList]);

  if (!isRPDDL || !dlList || dlList.length === 0) return null;

  const toggleDLExpand = async (idx) => {
    const isExpanding = !expandedDL[idx];
    setExpandedDL((prev) => ({ ...prev, [idx]: isExpanding }));
    if (isExpanding && documentId) {
      if (typeof handleRefreshTrackInterActivity === "function") {
        const refreshed = await handleRefreshTrackInterActivity(documentId);
        if (Array.isArray(refreshed) && refreshed.length > 0) {
          const built = buildDLListFromRecipients(refreshed, contactGroupsList);
          if (built.length > 0) {
            setDlList(built);
          }
        }
      } else {
        try {
          if (setOpen) setOpen(true);
          const url = `${process.env.REACT_APP_API + process.env.REACT_APP_API_GetTrackInteractivityData}/${documentId}`;
          const jsonDoc = await APIRequest.GET("", url);
          if (Array.isArray(jsonDoc) && jsonDoc.length > 0) {
            const built = buildDLListFromRecipients(jsonDoc, contactGroupsList);
            if (built.length > 0) {
              setDlList(built);
            }
          }
        } catch (err) {
          console.error("Error fetching track interactivity data:", err);
        } finally {
          if (setOpen) setOpen(false);
        }
      }
    }
  };

  const toggleMemberExpand = (dlIdx, memberIdx) => {
    const key = `${dlIdx}-${memberIdx}`;
    setExpandedMembers((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSingleMemberStatusToggle = async (e, dlIdx, member, uStatus) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!canEdit || member.status === "Pause") return;

    // Optimistic local update
    setDlList((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      const dl = updated[dlIdx];
      if (dl && dl.groupMembers) {
        const m = dl.groupMembers.find(
          (gm, idx) => gm.email === member.email || gm.id === member.id || idx === member.id
        );
        if (m) {
          m.enabled = uStatus;
          m.status = uStatus ? "Active" : "Banned";
          m.banned = !uStatus;
        }
      }
      if (onDLListChange) onDLListChange(updated);
      return updated;
    });

    try {
      setIsDLLoading(true);
      if (setOpen) setOpen(true);
      if (handlerUserActiveInactive) {
        await handlerUserActiveInactive(e, uStatus, documentId, member.email);
      } else {
        const url = `${process.env.REACT_APP_API + process.env.REACT_APP_API_RecipientActiveInactive}/${documentId}/${uStatus}/${member.email}`;
        const res = await APIRequest.POSTSaveSettings("", url);
        if (res && res === "Server maintenance") {
          window.location.href = "/maintenance";
          return;
        } else if (Number(res) === 200) {
          if (handleRefreshTrackInterActivity) {
            await handleRefreshTrackInterActivity(documentId);
          }
        }
      }
    } catch (err) {
      console.error("Error in handleSingleMemberStatusToggle:", err);
    } finally {
      setIsDLLoading(false);
      if (setOpen) setOpen(false);
    }
  };

  const handleMultiMemberStatusToggle = async (dlIdx, displayed, uStatus) => {
    if (!canEdit) return;
    const targetDL = filteredDLs[dlIdx];
    if (!targetDL) return;

    const checkedList = displayed
      .filter((m, mIdx) => {
        const memberKey = m.id || m.email || mIdx;
        return !!checkedMembers[`${dlIdx}-${memberKey}`] && m.email;
      })
      .map((m) => ({
        email: m.email,
        status: m.status || (m.enabled !== false ? "Active" : "Banned"),
        enabled: m.enabled
      }));

    let FinalSelectedReadersList = [];
    if (uStatus) {
      checkedList.forEach((el) => {
        if (el.status === "Banned" || el.enabled === false || el.enabled === "false") {
          FinalSelectedReadersList.push({
            docId: documentId,
            email: el.email
          });
        }
      });
    } else {
      checkedList.forEach((el) => {
        if (el.status === "Active" || (el.enabled !== false && el.enabled !== "false" && el.status !== "Banned")) {
          FinalSelectedReadersList.push({
            docId: documentId,
            email: el.email
          });
        }
      });
    }

    if (FinalSelectedReadersList.length > 0) {
      const emailsToUpdate = new Set(FinalSelectedReadersList.map((x) => x.email.toLowerCase()));
      setDlList((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        const dl = updated[dlIdx];
        if (dl && dl.groupMembers) {
          dl.groupMembers.forEach((m) => {
            if (m.email && emailsToUpdate.has(m.email.toLowerCase())) {
              m.enabled = uStatus;
              m.status = uStatus ? "Active" : "Banned";
              m.banned = !uStatus;
            }
          });
        }
        if (onDLListChange) onDLListChange(updated);
        return updated;
      });

      const url = `${process.env.REACT_APP_API + process.env.REACT_APP_API_RecipientActiveInactive}/${documentId}/${uStatus}/null`;
      try {
        setIsDLLoading(true);
        if (setOpen) setOpen(true);
        const res = await APIRequest.POSTSaveSettings(
          JSON.stringify(FinalSelectedReadersList),
          url
        );
        if (res && res === "Server maintenance") {
          window.location.href = "/maintenance";
          return;
        } else if (Number(res) === 200) {
          if (handleRefreshTrackInterActivity) {
            await handleRefreshTrackInterActivity(documentId);
          }
        }
      } catch (err) {
        console.error("Error in handleMultiMemberStatusToggle:", err);
      } finally {
        setIsDLLoading(false);
        if (setOpen) setOpen(false);
      }
    }

    // Clear checkboxes for this DL's displayed members
    setCheckedMembers((prev) => {
      const next = { ...prev };
      displayed.forEach((m, mIdx) => {
        const memberKey = m.id || m.email || mIdx;
        delete next[`${dlIdx}-${memberKey}`];
      });
      return next;
    });
  };

  // Search filtering
  const q = (searchQuery || "").toLowerCase().trim();
  const filteredDLs = dlList.filter((dl) => {
    if (!q) return true;
    const dlEmail = (dl.email || "").toLowerCase();
    const displayName = getDLDisplayName(dl).toLowerCase();
    if (dlEmail.includes(q) || displayName.includes(q)) return true;
    const members = (dl.groupMembers || []).filter((m) => Boolean((m?.code || "").toString().trim()));
    return members.some((m) => {
      const mEmail = (m.email || "").toLowerCase();
      const first = (m.firstName || "").toLowerCase();
      const last = (m.lastName || "").toLowerCase();
      return mEmail.includes(q) || first.includes(q) || last.includes(q);
    });
  });

  return (
    <div className="distribution-lists-section mb-3">
      {isDLLoading && <LoadingSpinner />}
      {/* Scoped CSS guaranteeing exact Material Icons arrow on first child td */}
      <style>{`
        .distribution-lists-section td.innertTableTd > table > tr.view td:first-child,
        .distribution-lists-section td.innertTableTd > table > tbody > tr.view td:first-child {
          position: relative !important;
          padding-left: 35px !important;
          cursor: pointer;
        }

        .distribution-lists-section td.innertTableTd > table > tr.view td:first-child:before,
        .distribution-lists-section td.innertTableTd > table > tbody > tr.view td:first-child:before {
          position: absolute !important;
          top: 50% !important;
          left: 5px !important;
          width: 10px !important;
          height: 16px !important;
          margin-top: -16px !important;
          color: #000 !important;
          font-size: 22px !important;
          font-family: "Material Icons" !important;
          content: "\\e037" !important;
          transition: all 0.3s ease !important;
          cursor: pointer !important;
        }

        .distribution-lists-section td.innertTableTd > table > tr.view.open td:first-child:before,
        .distribution-lists-section td.innertTableTd > table > tbody > tr.view.open td:first-child:before {
          transform: rotate(90deg) !important;
          position: absolute !important;
          color: #000000c7 !important;
          left: 16px !important;
          top: 50% !important;
        }

        .distribution-lists-section td.innertTableTd > table > tr.view.open,
        .distribution-lists-section td.innertTableTd > table > tbody > tr.view.open {
          background: #e6f4ff;
        }
      `}</style>

      {/* Section Header */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="fw-bold mb-0 d-flex align-items-center text-dark font-14">
          <span>{selectedLanguage?.LstLanguageCodes?.UI_DistributionLists}</span>
          <span
            className="badge rounded-pill bg-light text-secondary ms-2 border"
            style={{ fontSize: "11px", fontWeight: "700" }}
          >
            {filteredDLs.length}
          </span>
        </h6>
      </div>

      {/* Distribution Lists Table */}
      <div className="innertable-responsive table-container trakintrativity">
        <table className="trackInteractivity-table inertable box-shadow-light bg-white header-fixed">
          <thead className="bg-lightred">
            <tr>
              <th className="w-5p vertical-top borderRight" style={{ width: "5%", minWidth: "40px" }}></th>
              <th className="borderRight thead-th" style={{ width: "45%" }}>
                {selectedLanguage?.LstLanguageCodes?.UI_EmailAdressCaps}
              </th>
              <th className="borderRight thead-th" style={{ width: "30%" }}>
                {selectedLanguage?.LstLanguageCodes?.UI_AllowedDomains}
              </th>
              <th className="thead-th text-center" style={{ width: "20%" }}>
                {selectedLanguage?.LstLanguageCodes?.UI_NumberOfUsers}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredDLs.length === 0 ? (
              <tr className="borderrow">
                <td colSpan={4}>
                  <div className="d-flex py-3 align-items-center borderrow justify-content-center">
                    <span className="material-icons">report_problem</span>
                    <span className="">
                      {selectedLanguage?.LstLanguageCodes?.UI_NoRecordsFound}
                    </span>
                  </div>
                </td>
              </tr>
            ) : (
              filteredDLs.map((dl, dlIdx) => {
                const isExpanded = !!expandedDL[dlIdx];
                const domains = getDLAllowedDomains(dl);
                const members = (dl.groupMembers || []).filter((m) => Boolean((m?.code || "").toString().trim()));

                // Filter members by search query for this DL
                let displayedMembers = members;
                const dlMemberQuery = (memberSearch[dlIdx] || "").trim().toLowerCase();
                if (dlMemberQuery) {
                  displayedMembers = displayedMembers.filter((m) => {
                    return (
                      (m.email && m.email.toLowerCase().includes(dlMemberQuery)) ||
                      (m.firstName && m.firstName.toLowerCase().includes(dlMemberQuery)) ||
                      (m.lastName && m.lastName.toLowerCase().includes(dlMemberQuery))
                    );
                  });
                }

                // Sort members for this DL
                // Sort members for this DL using exact individual readers sort fields
                const currentSort = memberSort[dlIdx] || { value: "first_read", order: "desc" };
                if (currentSort && currentSort.value) {
                  displayedMembers = [...displayedMembers].sort((a, b) => {
                    if (currentSort.value === "first_read") {
                      const dateA = a.openedTime ? new Date(a.openedTime) : null;
                      const dateB = b.openedTime ? new Date(b.openedTime) : null;
                      if (!dateA && !dateB) return 0;
                      if (!dateA) return currentSort.order === "desc" ? 1 : -1;
                      if (!dateB) return currentSort.order === "desc" ? -1 : 1;
                      return currentSort.order === "desc" ? dateB - dateA : dateA - dateB;
                    } else if (currentSort.value === "total_time") {
                      const getTimeInMilliseconds = (timeStr) => {
                        if (!timeStr || timeStr === "00:00:00") return 0;
                        const [hours, minutes, seconds] = timeStr.split(":").map(Number);
                        return (hours * 3600 + minutes * 60 + seconds) * 1000;
                      };
                      return currentSort.order === "desc"
                        ? getTimeInMilliseconds(b.totalTime) - getTimeInMilliseconds(a.totalTime)
                        : getTimeInMilliseconds(a.totalTime) - getTimeInMilliseconds(b.totalTime);
                    } else if (currentSort.value === "reads") {
                      const countA = Number(a.readCount) || 0;
                      const countB = Number(b.readCount) || 0;
                      return currentSort.order === "desc" ? countB - countA : countA - countB;
                    } else if (currentSort.value === "days_since_firstAuth") {
                      const authA = Number(a.daysFirstAuthrized) || 0;
                      const authB = Number(b.daysFirstAuthrized) || 0;
                      return currentSort.order === "desc" ? authB - authA : authA - authB;
                    } else if (currentSort.value === "days_since_first") {
                      const firstA = Number(a.daysSinceFirstView) || 0;
                      const firstB = Number(b.daysSinceFirstView) || 0;
                      return currentSort.order === "desc" ? firstB - firstA : firstA - firstB;
                    } else if (currentSort.value === "days_since_last") {
                      const lastA = Number(a.daysSinceLastView) || 0;
                      const lastB = Number(b.daysSinceLastView) || 0;
                      return currentSort.order === "desc" ? lastB - lastA : lastA - lastB;
                    }
                    return 0;
                  });
                }

                return (
                  <React.Fragment key={dl.id || dl.email || dlIdx}>
                    {/* DL Row */}
                    <tr className="borderrow">
                      <td colSpan={4} className="innertTableTd" style={{ padding: "0em" }}>
                        <table className="table-layout" cellPadding="0" cellSpacing="0" style={{ width: "100%", tableLayout: "fixed" }}>
                          <tr
                            className={`borderrow text-wrap-custom ${isExpanded ? "open view" : "view"}`}
                            onClick={() => {
                              toggleDLExpand(dlIdx);
                            }}
                            // onDoubleClick={() => setSelectedDLForDetails(dl)}
                            // title="Double click to view domains & users"
                            style={{ cursor: "pointer" }}
                          >
                            <td
                              className="w-5p text-center"
                              style={{ width: "5%", minWidth: "40px", cursor: "pointer", paddingTop: "12px", paddingBottom: "12px", verticalAlign: "middle" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDLExpand(dlIdx);
                              }}
                            ></td>

                            <td style={{ width: "45%", paddingTop: "12px", paddingBottom: "12px", verticalAlign: "middle" }}>
                              <div className="d-flex align-items-center">
                                <span className="font-w600 font-14 text-dark text-nowrap" title={getDLDisplayName(dl)}>
                                  {getDLDisplayName(dl)}
                                </span>
                              </div>
                              {/* <div className="text-muted font-11 text-nowrap" style={{ marginTop: "4px" }}>
                                Double click to view domains & users
                              </div> */}
                            </td>

                            <td style={{ width: "30%", paddingTop: "12px", paddingBottom: "12px", verticalAlign: "middle" }}>
                              {domains && domains.length > 0 ? (
                                <div className="align-items-center flex-wrap textwrap tootltip-history d-flex">
                                  <span className="Wordbreak font-14 text-dark">
                                    {domains[0]}
                                  </span>

                                  <strong
                                    className={
                                      "seeMore" +
                                      (domains.length > 1
                                        ? ""
                                        : " d-none ")
                                    }
                                    id={`dlAllowedDomains_${(dl.id || dl.email || dlIdx).toString().replace(/[^a-zA-Z0-9-_]/g, "_")}`}
                                    data-html="true"
                                    style={{ paddingLeft: "5px", cursor: "pointer" }}
                                    onClick={(e) => e.stopPropagation()}>
                                    <i className="material-icons">
                                      more_horiz
                                    </i>
                                  </strong>

                                  {domains.length > 1 && (
                                    <ReactTooltip
                                      className="seemore-tooltip"
                                      anchorId={`dlAllowedDomains_${(dl.id || dl.email || dlIdx).toString().replace(/[^a-zA-Z0-9-_]/g, "_")}`}
                                      clickable>
                                      <table className="tooltip-table timeintractivity-table">
                                        <div className="tooltip-intial-receipts ps-2 pb-2 pt-1">
                                          {domains.map((dom, dIdx) => (
                                            <div
                                              key={dIdx}
                                              style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                whiteSpace: "normal",
                                                width: "100%",
                                              }}>
                                              <span
                                                style={{
                                                  wordBreak: "break-word",
                                                }}>
                                                {dom}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </table>
                                    </ReactTooltip>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted font-12">-</span>
                              )}
                            </td>

                            <td className="text-center" style={{ width: "20%", paddingTop: "12px", paddingBottom: "12px", verticalAlign: "middle" }}>
                              <span className="font-w600 font-14 text-dark">{members.length}</span>
                            </td>
                          </tr>

                          {/* Authenticated Users Sub-Table inside DL */}
                          {isExpanded && (
                            <tr className="fold open">
                              <td colSpan={4} style={{ padding: "0em" }}>
                                <div
                                  className="fold-content"
                                  style={{
                                    margin: "0px",
                                    backgroundColor: "#fbfbfe",
                                    borderTop: "1px solid #e0dddd",
                                    boxShadow: "none"
                                  }}
                                >
                                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ paddingTop: "14px", paddingBottom: "10px", paddingLeft: "12px", paddingRight: "12px" }}>
                                    <h6
                                      className="mb-0 text-dark font-13 fw-bold"
                                      style={{
                                        letterSpacing: "0.2px"
                                      }}
                                    >
                                      {/* {selectedLanguage?.LstLanguageCodes?.UI_AuthenticatedUsersviaAllowedDomains} */}
                                    </h6>

                                    <div className="manage-sortby-text d-flex align-items-center flex-shrink-0 ms-auto">
                                      {displayedMembers.some((m, mIdx) => !!checkedMembers[`${dlIdx}-${m.id || m.email || mIdx}`]) && (
                                        <span className="d-flex align-items-center" style={{ marginRight: "8px" }}>
                                          <input className="btn btn-outline-danger checked-btn track-btns" style={{ marginTop: "0px", marginBottom: "0px" }}
                                            onClick={() => handleMultiMemberStatusToggle(dlIdx, displayedMembers, false)} type="button" value={selectedLanguage?.LstLanguageCodes?.UI_BanSelected} />
                                          <input className="btn btn-outline-success checked-btn track-btns" style={{ marginTop: "0px", marginBottom: "0px" }}
                                            onClick={() => handleMultiMemberStatusToggle(dlIdx, displayedMembers, true)} type="button" value={selectedLanguage?.LstLanguageCodes?.UI_ActivateSelected} />
                                        </span>
                                      )}
                                      {members && members.length > 1 && (
                                        <input
                                          type="search"
                                          className="search-Reader-FirstNameLastName mr-12p"
                                          style={{
                                            height: "34px",
                                            fontSize: "12px",
                                            padding: "4px 8px",
                                            width: "220px",
                                            border: "1px solid #ced4da",
                                            borderRadius: "4px",
                                            marginBottom: "0px"
                                          }}
                                          placeholder={
                                            selectedLanguage?.LstLanguageCodes?.UI_EnterEmailOrFirstNameOrLastNameToSearch}
                                          value={memberSearch[dlIdx] || ""}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            setMemberSearch((prev) => ({ ...prev, [dlIdx]: val }));
                                          }}
                                        />
                                      )}

                                      {currentSort && (currentSort.value === "days_since_firstAuth" || currentSort.value === "days_since_first" || currentSort.value === "days_since_last") && (
                                        <span className="ms-2" style={{ marginTop: "-5px" }}>
                                          {selectedLanguage?.LstLanguageCodes?.UI_SortBy}:{" "}
                                          {currentSort.value === "days_since_firstAuth"
                                            ? selectedLanguage?.LstLanguageCodes?.UI_DaysSinceFirstAuthorized
                                            : (currentSort.value === "days_since_first"
                                              ? selectedLanguage?.LstLanguageCodes?.UI_DaysSinceFirstSActivity
                                              : selectedLanguage?.LstLanguageCodes?.UI_DaysSinceLastActivity)}
                                          <img
                                            src={currentSort.order === "desc" ? "assets/img/icons/down.png" : "assets/img/icons/up.png"}
                                            className="m-h-15p"
                                            alt="Sort"
                                          />
                                        </span>
                                      )}

                                      <img
                                        src="assets/img/icons/sort.png"
                                        alt="Sort"
                                        style={{
                                          cursor: "pointer",
                                          height: "30px",
                                          marginLeft: "8px"
                                        }}
                                        onClick={() => handleOpenDLSortPopup(dlIdx)}
                                        title={selectedLanguage?.LstLanguageCodes?.UI_SortBy}
                                      />
                                    </div>
                                  </div>

                                  <div className="innertable-responsive table-container trakintrativity">
                                    <table className="trackInteractivity-table inertable box-shadow-light bg-white header-fixed">
                                      <thead className="bg-lightred">
                                        <tr>
                                          <th className="w-5p vertical-top borderRight">
                                            {(() => {
                                              const isDLSelectAllDisabled = !canEdit || displayedMembers.length === 0;
                                              return (
                                                <div className="checkbox res-ml ms-44" style={{ cursor: isDLSelectAllDisabled ? "not-allowed" : "pointer", pointerEvents: "auto", }}>
                                                  <label className="p-0 mb-0" style={{ position: "relative", cursor: isDLSelectAllDisabled ? "not-allowed" : "pointer", pointerEvents: "auto", }}>
                                                    <input
                                                      type="checkbox"
                                                      checked={
                                                        displayedMembers.length > 0 &&
                                                        displayedMembers.every((m, mIdx) => checkedMembers[`${dlIdx}-${m.id || m.email || mIdx}`])
                                                      }
                                                      disabled={isDLSelectAllDisabled}
                                                      onChange={(e) => {
                                                        const isChecked = e.target.checked;
                                                        const newChecked = { ...checkedMembers };
                                                        displayedMembers.forEach((m, mIdx) => {
                                                          newChecked[`${dlIdx}-${m.id || m.email || mIdx}`] = isChecked;
                                                        });
                                                        setCheckedMembers(newChecked);
                                                      }}
                                                    />
                                                    <span className="cr" style={{ cursor: isDLSelectAllDisabled ? "not-allowed" : "pointer", }}>
                                                      <i className="cr-icon material-icons">check</i>
                                                    </span>
                                                  </label>
                                                </div>
                                              );
                                            })()}
                                          </th>
                                          <th className="borderRight thead-th w-18p">
                                            {selectedLanguage?.LstLanguageCodes?.UI_Adress}
                                          </th>
                                          <th className="borderRight w-10p thead-th">
                                            {selectedLanguage?.LstLanguageCodes?.UI_FirstName}
                                          </th>
                                          <th className="borderRight w-10p thead-th">
                                            {selectedLanguage?.LstLanguageCodes?.UI_LastName}
                                          </th>
                                          <th className="borderRight w-12p thead-th">
                                            {selectedLanguage?.LstLanguageCodes?.UI_Mobile}
                                          </th>
                                          <th className="thead-th borderRight w-12p">
                                            {selectedLanguage?.LstLanguageCodes?.UI_FirstRead}{" "}
                                            ({timeZoneAbbrevation?.current})
                                            {currentSort && currentSort.value === "first_read" && (
                                              <img
                                                src={currentSort.order === "desc" ? "assets/img/icons/down.png" : "assets/img/icons/up.png"}
                                                className="m-h-15p ms-1"
                                                alt="Sort"
                                              />
                                            )}
                                          </th>
                                          <th className="borderRight thead-th w-6p">
                                            {selectedLanguage?.LstLanguageCodes?.UI_Reads}
                                            {currentSort && currentSort.value === "reads" && (
                                              <img
                                                src={currentSort.order === "desc" ? "assets/img/icons/down.png" : "assets/img/icons/up.png"}
                                                className="m-h-15p ms-1"
                                                alt="Sort"
                                              />
                                            )}
                                          </th>
                                          <th className="borderRight thead-th w-10p">
                                            {selectedLanguage?.LstLanguageCodes?.UI_TotalMinutes}
                                            <>
                                              <span
                                                className="material-icons info-TotalTime ms-1"
                                                id={`timeFormat_dl_${(documentId).toString().replace(/[^a-zA-Z0-9-_]/g, "_")}_${dlIdx}`}
                                                data-toggle="tooltip"
                                                data-html="true"
                                                style={{ position: "relative", top: "5px", fontSize: "18px", color: "#356fcd", cursor: "pointer" }}
                                              >
                                                info
                                              </span>
                                              <ReactTooltip
                                                className="totalTime-tooltip"
                                                style={{
                                                  opacity: 1,
                                                }}
                                                anchorId={`timeFormat_dl_${(documentId).toString().replace(/[^a-zA-Z0-9-_]/g, "_")}_${dlIdx}`}
                                                place="bottom"
                                              >
                                                <table className="tooltip-table">
                                                  <tbody>
                                                    <tr>
                                                      <td>
                                                        <div className="d-flex flex-column p-1">
                                                          <p className="statistics-tooltip-text mb-0">
                                                            {selectedLanguage?.LstLanguageCodes?.UI_TotalTimeFormat}
                                                          </p>
                                                        </div>
                                                      </td>
                                                    </tr>
                                                  </tbody>
                                                </table>
                                              </ReactTooltip>
                                            </>
                                            {currentSort && currentSort.value === "total_time" && (
                                              <img
                                                src={currentSort.order === "desc" ? "assets/img/icons/down.png" : "assets/img/icons/up.png"}
                                                className="m-h-15p ms-1"
                                                alt="Sort"
                                              />
                                            )}
                                          </th>
                                          <th className="borderRight thead-th w-6p">
                                            {selectedLanguage?.LstLanguageCodes?.UI_Vote}
                                          </th>
                                          <th className="thead-th w-10p">
                                            {selectedLanguage?.LstLanguageCodes?.UI_Status}
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody style={{ maxHeight: "none", overflow: "visible" }}>
                                        {displayedMembers.length === 0 ? (
                                          <tr style={{ display: "block", width: "100%" }}>
                                            <td colSpan={10} style={{ display: "block", width: "100%", padding: "0px", border: "none" }}>
                                              <div className="no-record mt-n1 d-flex align-items-center justify-content-center w-100" style={{ width: "100%" }}>
                                                <span className="material-icons">report_problem</span>
                                                <span className="">{selectedLanguage?.LstLanguageCodes?.UI_NoRecordsFound}</span>
                                              </div>
                                            </td>
                                          </tr>
                                        ) : (
                                          displayedMembers.map((member, mIdx) => {
                                            const memberKey = member.id || member.email || mIdx;
                                            const isMemberExp = !!expandedMembers[`${dlIdx}-${memberKey}`];
                                            const isMemberChecked = !!checkedMembers[`${dlIdx}-${memberKey}`];
                                            return (
                                              <tr className="borderrow" key={memberKey}>
                                                <td colSpan={10} className="innertTableTd">
                                                  <table className="table-layout" cellPadding="0" cellSpacing="0" style={{ width: "100%" }}>
                                                    <tr
                                                      className={`borderrow text-wrap-custom ${isMemberExp ? "open view" : "view"}`}
                                                      onClick={(e) => {
                                                        if (
                                                          e.target.className === "cr-icon material-icons" ||
                                                          e.target.className === "p-0 mb-0" ||
                                                          e.target.type === "checkbox"
                                                        ) {
                                                          return;
                                                        }
                                                        toggleMemberExpand(dlIdx, memberKey);
                                                      }}
                                                      style={{ cursor: "pointer" }}
                                                    >
                                                      <td
                                                        className="w-5p text-center"
                                                        style={{ display: "", cursor: "pointer" }}
                                                        onClick={(e) => {
                                                          if (
                                                            e.target.className === "cr-icon material-icons" ||
                                                            e.target.className === "p-0 mb-0" ||
                                                            e.target.type === "checkbox"
                                                          ) {
                                                            e.stopPropagation();
                                                            return;
                                                          }
                                                          e.stopPropagation();
                                                          toggleMemberExpand(dlIdx, memberKey);
                                                        }}
                                                      >
                                                        <div className="checkbox mt-n5p marginTop8 res-ml-n8" onClick={(e) => e.stopPropagation()}>
                                                          <label className="p-0 mb-0" style={{ cursor: "pointer" }}>
                                                            <input
                                                              type="checkbox"
                                                              checked={isMemberChecked}
                                                              onChange={() =>
                                                                setCheckedMembers((prev) => ({
                                                                  ...prev,
                                                                  [`${dlIdx}-${memberKey}`]: !prev[`${dlIdx}-${memberKey}`]
                                                                }))
                                                              }
                                                            />
                                                            <span className="cr">
                                                              <i className="cr-icon material-icons">check</i>
                                                            </span>
                                                          </label>
                                                        </div>
                                                      </td>
                                                      <td className="w-18p">
                                                        <div className="firstNameLastNameMobile mail-wrap AllItems-th">
                                                          {member.email}
                                                        </div>
                                                      </td>
                                                      <td className="w-10p">
                                                        <div className="firstNameLastNameMobile d-flex flex-wrap textwrap">
                                                          <span className="Wordbreak firstNameLastNameMobile">
                                                            {member.firstName || ""}
                                                          </span>
                                                        </div>
                                                      </td>
                                                      <td className="text-center w-10p">
                                                        <div className="firstNameLastNameMobile d-flex flex-wrap textwrap">
                                                          <span className="Wordbreak firstNameLastNameMobile">
                                                            {member.lastName || ""}
                                                          </span>
                                                        </div>
                                                      </td>
                                                      <td className="text-center w-12p">
                                                        <div className="firstNameLastNameMobile d-flex flex-wrap textwrap">
                                                          <span className="Wordbreak">
                                                            {member.mobileCountryCode && member.mobileNumber
                                                              ? `${member.mobileCountryCode} ${member.mobileNumber}`
                                                              : (member.mobileNumber !== "-" ? member.mobileNumber : "")}
                                                          </span>
                                                        </div>
                                                      </td>
                                                      <td className="pcs w-12p">
                                                        {member.openedTime
                                                          ? (applyTimeZoneToDisplay && timeZoneRef
                                                            ? moment.utc(
                                                              applyTimeZoneToDisplay(
                                                                new Date(member.openedTime),
                                                                timeZoneRef.current
                                                              )
                                                            ).format(`${dateFormatValueUpper} HH:mm`)
                                                            : moment(member.openedTime).format("DD/MM/YYYY HH:mm"))
                                                          : ""}
                                                      </td>
                                                      <td className="per w-6p">
                                                        {member.readCount !== undefined ? member.readCount : 0}
                                                      </td>
                                                      <td className="cur w-10p">
                                                        {member.totalTime || "00:00:00"}
                                                      </td>
                                                      <td className="w-6p text-center">
                                                        {member.vote === 1 ? (
                                                          <span className="material-icons me-2 colorGreen">thumb_up_alt</span>
                                                        ) : member.vote === 0 ? (
                                                          <span className="material-icons me-2 colorRed">thumb_down_alt</span>
                                                        ) : (
                                                          "-"
                                                        )}
                                                      </td>
                                                      <td className="per w-10p text-center" onClick={(e) => e.stopPropagation()}>
                                                        {member.email && (
                                                          (member.enabled !== false && member.status !== "Banned" && member.status !== "Pause") ? (
                                                            <button
                                                              style={{ padding: "5px 18px 5px 18px" }}
                                                              type="button"
                                                              className={canEdit ? "btn btn-outline-success track-btn" : "btn btn-outline-success disabled track-btn"}
                                                              onClick={(e) => canEdit ? handleSingleMemberStatusToggle(e, dlIdx, member, false) : undefined}
                                                            >
                                                              {selectedLanguage?.LstLanguageCodes?.UI_Active}
                                                            </button>
                                                          ) : (
                                                            <div
                                                              title={member.status === "Pause" ? "The Reader status cannot be altered while the RPD is in an Expired state." : ""}
                                                              style={{ cursor: member.status === "Pause" || !canEdit ? "not-allowed" : "pointer" }}
                                                            >
                                                              <button
                                                                style={{
                                                                  padding: member.status === "Pause" ? "5px 20px 5px 20px" : "5px 13px",
                                                                  cursor: member.status === "Pause" || !canEdit ? "not-allowed" : "pointer"
                                                                }}
                                                                type="button"
                                                                className={
                                                                  member.status === "Pause"
                                                                    ? "btn btn-outline-danger Pause disabled track-btn"
                                                                    : canEdit
                                                                      ? "btn btn-outline-danger track-btn"
                                                                      : "btn btn-outline-danger disabled track-btn"
                                                                }
                                                                onClick={(e) => (canEdit && member.status !== "Pause") ? handleSingleMemberStatusToggle(e, dlIdx, member, true) : undefined}
                                                              >
                                                                {member.status === "Pause"
                                                                  ? (selectedLanguage?.LstLanguageCodes?.UI_Pause)
                                                                  : (selectedLanguage?.LstLanguageCodes?.UI_Banned)}
                                                              </button>
                                                            </div>
                                                          )
                                                        )}
                                                      </td>
                                                    </tr>

                                                    {/* Expanded Member Details: EXACT cardborder FROM Manage.js */}
                                                    {isMemberExp && (
                                                      <tr className="fold open">
                                                        <td colSpan={10} style={{ padding: "0em" }}>
                                                          <div className="fold-content" style={{ padding: "0em", boxShadow: "none" }}>
                                                            <div className="row mx-0 cardborder align-items-start">
                                                              {/* Left Column: Days Since, Reader Statistics, Print Count */}
                                                              <div className="col-sm-12 col-md-5 ps-1">
                                                                <div className="d-flex flex-column">
                                                                  <h6 className="font-w600 daysince mb-0 text-dec w-auto">
                                                                    <span className="material-icons me-1">date_range</span>
                                                                    <span>
                                                                      {selectedLanguage?.LstLanguageCodes?.UI_DaysSince}
                                                                    </span>
                                                                  </h6>
                                                                  <div className="d-flex align-itmes-center py-0 tracktext">
                                                                    <div className="d-flex align-items-center">
                                                                      <span className="font-w400">
                                                                        {selectedLanguage?.LstLanguageCodes
                                                                          ?.UI_DaysSinceFirstAuthorized}
                                                                        :
                                                                      </span>
                                                                      <span className="ms-2" style={{ fontWeight: "600" }}>
                                                                        {member.daysFirstAuthrized !== undefined
                                                                          ? member.daysFirstAuthrized
                                                                          : 20}
                                                                      </span>
                                                                    </div>
                                                                    <div className="d-flex align-items-center mx-3">
                                                                      <span className="font-w400">
                                                                        {selectedLanguage?.LstLanguageCodes
                                                                          ?.UI_DaysSinceFirstSActivity}
                                                                        :
                                                                      </span>
                                                                      <span className="ms-2" style={{ fontWeight: "600" }}>
                                                                        {member.daysSinceFirstView !== undefined
                                                                          ? member.daysSinceFirstView
                                                                          : 20}
                                                                      </span>
                                                                    </div>
                                                                    <div className="d-flex align-items-center">
                                                                      <span className="font-w400">
                                                                        {selectedLanguage?.LstLanguageCodes
                                                                          ?.UI_DaysSinceLastActivity}
                                                                        :
                                                                      </span>
                                                                      <span className="ms-2" style={{ fontWeight: "600" }}>
                                                                        {member.daysSinceLastView !== undefined
                                                                          ? member.daysSinceLastView
                                                                          : 20}
                                                                      </span>
                                                                    </div>
                                                                  </div>

                                                                  {member.email && (
                                                                    <div
                                                                      style={{ fontWeight: "400" }}
                                                                      className="d-flex align-items-center"
                                                                    >
                                                                      <span
                                                                        style={{ color: "#d21513" }}
                                                                        className="material-icons me-1"
                                                                      >
                                                                        stacked_bar_chart
                                                                      </span>
                                                                      <span>
                                                                        {selectedLanguage?.LstLanguageCodes?.UI_ReaderStatisticsbyPage}:
                                                                      </span>
                                                                      <button
                                                                        type="button"
                                                                        style={{
                                                                          color: "#3045c0fc",
                                                                          cursor: "pointer",
                                                                          background: "none",
                                                                          border: "none",
                                                                          padding: 0
                                                                        }}
                                                                        className="ms-1 fw-medium font-13"
                                                                        onClick={() => {
                                                                          if (onOpenReaderStats) {
                                                                            onOpenReaderStats(member.email);
                                                                          } else {
                                                                            setSelectedMemberForStats(member.email);
                                                                          }
                                                                        }}
                                                                      >
                                                                        {selectedLanguage?.LstLanguageCodes?.UI_View}
                                                                      </button>
                                                                    </div>
                                                                  )}

                                                                  <div className="text-start d-flex tracktext">
                                                                    <span className="font-w400">
                                                                      {selectedLanguage?.LstLanguageCodes?.UI_PrintCount}
                                                                      :
                                                                    </span>
                                                                    <span className="ms-2 font-w600">
                                                                      {member.printCount !== undefined ? member.printCount : 0}
                                                                    </span>
                                                                  </div>
                                                                </div>
                                                              </div>

                                                              {/* Right Column: E-Disclosure, Reference, Verification, Max prints */}
                                                              <div className="col-md-7 col-sm-12 text-start d-flex flex-column">
                                                                {member.eDisclosureAgreed !== undefined ? (
                                                                  <div className="col-sm-12 text-start d-flex tracktext">
                                                                    <span className="font-w400 w-38">
                                                                      {selectedLanguage?.LstLanguageCodes?.UI_EDisclosureStatus} {":"}
                                                                    </span>
                                                                    <span className="ms-1" style={{ fontWeight: '600' }}>
                                                                      {member.eDisclosureAgreed !== null ? (
                                                                        <>
                                                                          {member.eDisclosureAgreed ? selectedLanguage?.LstLanguageCodes?.UI_Agreed : selectedLanguage?.LstLanguageCodes?.UI_DisAgreed}
                                                                          {member.eDisclosureTime && (
                                                                            <>
                                                                              &nbsp;[
                                                                              {moment.utc(
                                                                                applyTimeZoneToDisplay(
                                                                                  new Date(member.eDisclosureTime),
                                                                                  timeZoneRef?.current
                                                                                )
                                                                              ).format(`${dateFormatValueUpper} HH:mm`)}
                                                                              &nbsp;{timeZoneAbbrevation?.current || timeZoneAbbrevation || ""}]
                                                                            </>
                                                                          )}
                                                                        </>
                                                                      ) : ""}
                                                                    </span>
                                                                  </div>
                                                                ) : ""}
                                                                <div className="col-sm-12 text-start d-flex tracktext">
                                                                  <span className="font-w400 w-38">Reference:</span>
                                                                  <span className="ms-1" style={{ fontWeight: "600" }}>
                                                                    {member.reference || ""}
                                                                  </span>
                                                                </div>
                                                                <div className="col-sm-12 text-start d-flex tracktext">
                                                                  <span className="font-w400 w-38">Send verification code:</span>
                                                                  <span className="ms-1" style={{ fontWeight: "600" }}>
                                                                    {member.authType || ""}
                                                                  </span>
                                                                </div>
                                                                <div className="col-sm-12 text-start d-flex tracktext">
                                                                  <span className="font-w400 w-38">Max Allowed Prints:</span>
                                                                  <span className="ms-1" style={{ fontWeight: "600" }}>
                                                                    {member.maxPrintsPerReader || ""}
                                                                  </span>
                                                                </div>
                                                              </div>
                                                            </div>
                                                          </div>
                                                        </td>
                                                      </tr>
                                                    )}
                                                  </table>
                                                </td>
                                              </tr>
                                            );
                                          })
                                        )}
                                      </tbody>
                                      <tfoot>
                                        <tr>
                                          <td colSpan={10}>
                                            <div className="d-flex justify-content-end p-2 align-items-center">
                                              <span className="font-13">Total : </span>
                                              <span className="material-icons colorGreen ms-2 font-18">thumb_up_alt</span>
                                              <span className="ms-1 me-2 font-13">
                                                {members.filter((m) => m.vote === 1).length}
                                              </span>
                                              <span className="material-icons colorRed ms-1 font-18">thumb_down_alt</span>
                                              <span className="ms-1 font-13">
                                                {members.filter((m) => m.vote === 0).length}
                                              </span>
                                            </div>
                                          </td>
                                        </tr>
                                      </tfoot>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </table>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {selectedMemberForStats && (
        <DLReaderStatsModal
          isOpen={!!selectedMemberForStats}
          onClose={() => setSelectedMemberForStats(null)}
          documentId={documentId}
          documentName={documentName}
          readerEmail={selectedMemberForStats}
          selectedLanguage={selectedLanguage}
        />
      )}
      <TrackSortingPopup
        trackInteractivitySortingPopUp={dlSortingPopUp}
        sortOptions={dlSortOptions}
        toggleSortOrder={toggleDLSortOrder}
        handlerModelCancelSorting={handleCancelDLSorting}
        handlerModelOkSorting={handleOkDLSorting}
        selectedLanguage={selectedLanguage}
      />
    </div>
  );
}
